
import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import FavoriteButton from "@/components/common/FavoriteButton";
import { Favorite } from "@/hooks/useFavorites";
import { useTranslation } from "react-i18next";

interface Test {
  id: string;
  title: string;
  description: string;
}

interface FavoriteTestCardProps {
  favorite: Favorite;
  testDetails: Test;
  getTestRoute: (testType: string, testId: string) => string;
  getTestIcon: (testId: string) => React.ReactNode;
  getLevelName: (testType: string) => string;
  getLevelBadgeVariant: (testType: string) => "secondary" | "default" | "outline";
}

const FavoriteTestCard: React.FC<FavoriteTestCardProps> = ({
  favorite,
  testDetails,
  getTestRoute,
  getTestIcon,
  getLevelName,
  getLevelBadgeVariant
}) => {
  const { t } = useTranslation('training');
  
  // Get translation key based on test type and ID
  const getTranslatedContent = () => {
    const testType = favorite.test_type;
    const testId = testDetails.id;
    
    // Check if translation exists for this test
    const translationKey = `tests.${testType}.${testId}`;
    const translatedTitle = t(`${translationKey}.title`, { defaultValue: testDetails.title });
    const translatedDescription = t(`${translationKey}.description`, { defaultValue: testDetails.description });
    
    return {
      title: translatedTitle,
      description: translatedDescription
    };
  };
  
  const content = getTranslatedContent();
  
  return (
    <Link
      to={getTestRoute(favorite.test_type, favorite.test_id)}
      className="block"
    >
      <div className="border rounded-md p-4 hover:bg-gray-50 transition-colors relative group">
        <div className="flex items-center gap-2 mb-2">
          {getTestIcon(favorite.test_id)}
          <div className="flex-1">
            <h3 className="font-medium line-clamp-1">{content.title}</h3>
            <Badge variant={getLevelBadgeVariant(favorite.test_type)} className="mt-1 text-xs">
              {getLevelName(favorite.test_type)}
            </Badge>
          </div>
          <div className="absolute right-3 top-3">
            <FavoriteButton 
              testId={favorite.test_id} 
              testType={favorite.test_type as "level1" | "level2" | "level3" | "putting" | "approach" | "chipping" | "wedging"}
              size="icon"
              variant="ghost"
              className="opacity-70 hover:opacity-100"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 line-clamp-2">{content.description}</p>
      </div>
    </Link>
  );
};

export default FavoriteTestCard;
